/**
 * Ό,τι δείχνει η σελίδα «Κατάλογος» (δέντρο, λίστα, μετρήσεις), υπολογισμένο από το πρόχειρο πάνω
 * στη βάση. Οι αριθμοί του δέντρου = ΟΛΑ τα προϊόντα του κόμβου (και υποκατηγορίες, κρυφά, μη
 * διαθέσιμα) — όχι το product_count του API, που μετρά μόνο τα διαθέσιμα.
 */
import { autoCategoryTone } from '../../utils/catalogHelpers';
import { catalogTone, CatalogTone } from '../../utils/catalogColors';
import { formatEuro } from '../../utils/formatEuro';
import { FormSize, GroupRef, searchKey } from './catalogModel';
import {
  CatalogBase, CatalogDraft, baseProduct, currentCategoryForm, currentProduct, currentTree, nodeOfRef, orderedProducts,
} from './catalogDraft';

export interface ProductView {
  key: string;
  id: number | null;
  isNew: boolean;
  name: string;
  description: string;
  barcode: string;
  color: string | null;
  node: string;
  sizes: FormSize[];
  feeCategory: number | null;
  feeGross: number | null;
  openPrice: boolean;
  is_available: boolean;
  is_hidden: boolean;
  groups: GroupRef[] | null;
  pendingAvailable: boolean;
  pendingHidden: boolean;
}

export function productView(draft: CatalogDraft, base: CatalogBase, key: string): ProductView | null {
  const s = currentProduct(draft, base, key);
  if (!s) return null;
  const p = base.byKey[key];
  const orig = p ? baseProduct(base, key) : null;
  const gross = p?.fee_unit_gross;
  return {
    key,
    id: p ? p.id : null,
    isNew: !p,
    name: s.form.name,
    description: s.form.description,
    barcode: s.form.barcode,
    color: s.form.color,
    node: nodeOfRef(s.form.category_id, base.categories, draft),
    sizes: s.form.sizes,
    feeCategory: s.form.fee_category,
    feeGross: gross !== undefined && gross !== null && gross !== '' && Number.isFinite(Number(gross)) ? Number(gross) : null,
    openPrice: s.form.is_open_price,
    is_available: s.is_available,
    is_hidden: s.is_hidden,
    groups: s.groups,
    pendingAvailable: orig ? orig.is_available !== s.is_available : !s.is_available,
    pendingHidden: orig ? orig.is_hidden !== s.is_hidden : s.is_hidden,
  };
}

/** Όλα τα προϊόντα (server + νέα) ως προβολές. */
export function allViews(draft: CatalogDraft, base: CatalogBase): Record<string, ProductView> {
  const out: Record<string, ProductView> = {};
  base.products.forEach(p => { const v = productView(draft, base, `p:${p.id}`); if (v) out[v.key] = v; });
  draft.newProducts.forEach(k => { const v = productView(draft, base, k); if (v) out[k] = v; });
  return out;
}

/** Μέλη ανά κόμβο (στη φυσική σειρά: server, μετά τα νέα). */
export function membersByNode(views: Record<string, ProductView>, base: CatalogBase, draft: CatalogDraft): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  const push = (k: string) => { const v = views[k]; if (v) (out[v.node] = out[v.node] || []).push(k); };
  base.products.forEach(p => push(`p:${p.id}`));
  draft.newProducts.forEach(push);
  return out;
}

export interface TreeNodeView {
  key: string;
  name: string;
  tone: CatalogTone | null;
  explicitTone: boolean;
  count: number;
  hidden: boolean;
  isNew: boolean;
  children: TreeNodeView[];
}

/**
 * Ο τόνος που βλέπει το ταμείο: ρητός, αλλιώς ο αυτόματος (όνομα, αλλιώς θέση ανάμεσα στις ορατές
 * αδελφές — ίδιος κανόνας με το categoryFormAutoTone του παλιού παραθύρου).
 */
export function categoryTone(name: string, color: string | null | undefined, visibleSiblings: string[], key: string): CatalogTone {
  const explicit = catalogTone(color);
  if (explicit) return explicit;
  const at = visibleSiblings.indexOf(key);
  return autoCategoryTone(name, at >= 0 ? at : visibleSiblings.length);
}

export function treeView(draft: CatalogDraft, base: CatalogBase, members: Record<string, string[]>): TreeNodeView[] {
  const tree = currentTree(draft, base);
  const visible = (keys: string[]) => keys.filter(k => !currentCategoryForm(draft, base, k)?.is_hidden);
  const make = (key: string, siblings: string[], kids: string[]): TreeNodeView => {
    const form = currentCategoryForm(draft, base, key);
    const name = form?.name || '';
    const children = kids.map(k => make(k, visible(kids), []));
    return {
      key,
      name,
      tone: categoryTone(name, form?.color, siblings, key),
      explicitTone: !!catalogTone(form?.color),
      count: (members[key] || []).length + children.reduce((n, c) => n + c.count, 0),
      hidden: !!form?.is_hidden,
      isNew: draft.newCategories.includes(key),
      children,
    };
  };
  const roots = visible(tree.roots);
  return tree.roots.map(k => make(k, roots, tree.children[k] || []));
}

/** Τα προϊόντα κάθε κατηγορίας στη σειρά του ταμείου, με τη σειρά του δέντρου (για «Όλα»). */
export function allInTreeOrder(draft: CatalogDraft, base: CatalogBase, members: Record<string, string[]>): string[] {
  const tree = currentTree(draft, base);
  const out: string[] = [];
  tree.roots.forEach(root => {
    out.push(...orderedProducts(draft, base, root, members[root] || []));
    (tree.children[root] || []).forEach(child => out.push(...orderedProducts(draft, base, child, members[child] || [])));
  });
  out.push(...orderedProducts(draft, base, 'none', members.none || []));
  return out;
}

/** Αναζήτηση σε όλο τον κατάλογο: όνομα, περιγραφή, barcode — χωρίς τόνους και κεφαλαία. */
export function matchesSearch(v: ProductView, query: string): boolean {
  const q = searchKey(query.trim());
  if (!q) return true;
  return searchKey(v.name).includes(q) || searchKey(v.description).includes(q) || (!!v.barcode && searchKey(v.barcode).includes(q));
}

export type ListFilter = 'all' | 'available' | 'unavailable' | 'hidden';

export function passesFilter(v: ProductView, filter: ListFilter): boolean {
  if (filter === 'available') return v.is_available && !v.is_hidden;
  if (filter === 'unavailable') return !v.is_available;
  if (filter === 'hidden') return v.is_hidden;
  return true;
}

/* ───────────────────────── Τιμές στη λίστα ───────────────────────── */

const num = (n: number) => n.toFixed(2).replace('.', ',');

/** Τα μεγέθη που πουλάει το ταμείο (κρυφά/αποσυρμένα όχι). */
export const sellable = (sizes: FormSize[]) => sizes.filter(s => s.is_available !== false && s.is_hidden !== true);

export function priceSummary(v: ProductView, labels: { fee: string; openPrice: string; sizes: (n: number) => string; noSizes: string }): string {
  if (v.feeCategory !== null && v.feeCategory !== undefined) return v.feeGross !== null ? formatEuro(v.feeGross) : labels.fee;
  if (v.openPrice) return labels.openPrice;
  const s = sellable(v.sizes);
  if (s.length === 0) return labels.noSizes;
  if (s.length === 1) return formatEuro(s[0].price_modifier);
  if (s.length === 2) return `${s.map(x => `${x.name} ${num(x.price_modifier)}`).join(' · ')} €`;
  const prices = s.map(x => x.price_modifier);
  return `${labels.sizes(s.length)} · ${num(Math.min(...prices))}–${num(Math.max(...prices))} €`;
}

/** «Dine-In +0,50 · Delivery ανά μέγεθος» — πού διαφέρει η τιμή του καναλιού από την «Τιμή». */
export function channelSummary(sizes: FormSize[], labels: { takeAway: string; dineIn: string; delivery: string; perSize: string }): string {
  const s = sellable(sizes);
  if (s.length === 0) return '';
  const parts: string[] = [];
  const channels: Array<[keyof FormSize, string]> = [
    ['price_modifier_take_away', labels.takeAway],
    ['price_modifier_dine_in', labels.dineIn],
    ['price_modifier_delivery', labels.delivery],
  ];
  channels.forEach(([k, label]) => {
    const diffs = s.map(x => (x[k] === null || x[k] === undefined ? 0 : Math.round(((x[k] as number) - x.price_modifier) * 100) / 100));
    if (diffs.every(d => d === 0)) return;
    parts.push(diffs.every(d => d === diffs[0])
      ? `${label} ${diffs[0] > 0 ? '+' : '−'}${num(Math.abs(diffs[0]))}`
      : `${label} ${labels.perSize}`);
  });
  return parts.join(' · ');
}
